import { motion } from "framer-motion";
import { ArrowRight, BadgeCheck, Facebook, Instagram, Megaphone } from "lucide-react";
import { AUDIENCE_TRAITS, COLLAB_FORMATS, SOCIAL } from "../data/cv";
import { fadeUp, stagger, viewportOnce } from "../lib/anim";
import { scrollToId } from "./Nav";

const BRAND_SCOPE = "Brand & audience collaborations · 17K+ reach";

const proposeCollab = () => {
  window.dispatchEvent(new CustomEvent("select-scope", { detail: BRAND_SCOPE }));
  scrollToId("#engage");
};

export default function SocialLiterary() {
  return (
    <section id="social" className="relative py-16 sm:py-24 lg:py-32">
      <div className="absolute bottom-0 -right-40 w-[460px] h-[460px] rounded-full bg-neon/10 orb" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative">
        <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}>
          <motion.p variants={fadeUp} className="font-mono text-xs tracking-[0.22em] uppercase text-neon mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-gradient-to-r from-neon to-transparent" /> 05 — Audience &amp; reach
          </motion.p>
          <motion.h2 variants={fadeUp} custom={1} className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-3xl" data-testid="audience-heading">
            A 17,000-strong organic audience. <span className="text-grad">A credible voice for your brand.</span>
          </motion.h2>
          <motion.p variants={fadeUp} custom={2} className="mt-4 text-sm sm:text-base text-slate-400 max-w-2xl">
            17K+ combined followers across Instagram and Facebook — organic, non-bot and drawn from medicine and well
            beyond it. Available to brands, health-tech companies and organisations for promotion, branding and awareness
            campaigns.
          </motion.p>
        </motion.div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Audience asset */}
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className="lg:col-span-7 flex flex-col gap-6">
            <div className="panel spot-card rounded-2xl p-6 sm:p-8" data-testid="social-presence-card">
              <div className="flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
                <div>
                  <p className="font-mono text-[11px] tracking-[0.2em] uppercase text-slate-500">Total organic reach</p>
                  <p className="mt-1 font-display font-extrabold text-5xl text-grad" data-testid="total-audience-count">{SOCIAL.total}</p>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="font-display font-extrabold text-3xl text-grad" data-testid="instagram-follower-count">{SOCIAL.instagram.followers}</p>
                    <p className="text-[11px] font-mono text-slate-500 uppercase tracking-wider flex items-center gap-1 justify-center">
                      <Instagram size={10} /> Instagram
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="font-display font-extrabold text-3xl text-grad" data-testid="facebook-follower-count">{SOCIAL.facebook.followers}</p>
                    <p className="text-[11px] font-mono text-slate-500 uppercase tracking-wider flex items-center gap-1 justify-center">
                      <Facebook size={10} /> Facebook
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex items-center gap-2">
                <p className="font-display font-semibold">{SOCIAL.instagram.handle}</p>
                {SOCIAL.instagram.verified && (
                  <span className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-sky-400 border border-sky-400/30 rounded-full px-2 py-0.5" data-testid="meta-verified-badge">
                    <BadgeCheck size={11} /> Meta verified
                  </span>
                )}
              </div>

              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {AUDIENCE_TRAITS.map((t) => (
                  <div key={t.title} className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid="audience-trait">
                    <p className="text-sm font-semibold text-slate-100">{t.title}</p>
                    <p className="mt-1.5 text-xs text-slate-400 leading-relaxed">{t.text}</p>
                  </div>
                ))}
              </div>

              <div className="mt-7 flex flex-wrap gap-3">
                <button
                  onClick={proposeCollab}
                  data-testid="propose-collaboration-button"
                  className="group inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-ink bg-gradient-to-r from-neon to-vio hover:shadow-glow-cyan transition-shadow duration-300"
                >
                  Propose a collaboration
                  <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform duration-300" />
                </button>
                <a
                  href={SOCIAL.instagram.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="instagram-follow-button"
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white border border-fuchsia-400/40 bg-fuchsia-500/10 hover:bg-fuchsia-500/20 hover:border-fuchsia-400/70 transition-colors duration-300"
                >
                  <Instagram size={15} /> View {SOCIAL.instagram.handle}
                </a>
              </div>
            </div>

            {/* Photos */}
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-6">
              <motion.div variants={fadeUp} custom={1} className="sm:col-span-3 frame-spot clip-notch p-[2px] bg-gradient-to-br from-vio/40 to-neon/30" data-testid="photo-tile-award">
                <div className="clip-notch overflow-hidden h-full min-h-[240px] bg-panel">
                  <img src="/images/award.jpg" alt="Receiving BW Healthcare World 30 Under Thirty award" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </motion.div>
              <motion.div variants={fadeUp} custom={2} className="sm:col-span-2 frame-spot clip-notch p-[2px] bg-gradient-to-tl from-neon/40 to-vio/30" data-testid="photo-tile-police">
                <div className="clip-notch overflow-hidden h-full min-h-[240px] bg-panel">
                  <img src="/images/speak-police.jpg" alt="Addressing the Commissionerate Police" className="w-full h-full object-cover" loading="lazy" />
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* Collaboration formats */}
          <motion.div variants={fadeUp} custom={2} initial="hidden" whileInView="show" viewport={viewportOnce} className="lg:col-span-5">
            <div className="panel spot-card rounded-2xl p-6 sm:p-8 h-full flex flex-col" data-testid="collab-formats-card">
              <span className="grid place-items-center w-12 h-12 rounded-xl border border-white/10 bg-white/[0.04] text-neon">
                <Megaphone size={20} />
              </span>
              <h3 className="mt-6 font-display text-xl font-semibold tracking-tight">Partner with this audience.</h3>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                Content is produced and presented by a practising, Meta-verified consultant pathologist — accurate,
                credible and brand-safe for healthcare and consumer partners.
              </p>
              <p className="mt-7 font-mono text-[11px] tracking-[0.2em] uppercase text-slate-500">Collaboration formats</p>
              <ul className="mt-4 space-y-3">
                {COLLAB_FORMATS.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-slate-200" data-testid="collab-format-item">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gradient-to-r from-neon to-vio shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={proposeCollab}
                data-testid="collab-card-cta-button"
                className="mt-auto pt-8 inline-flex items-center gap-2 text-sm font-semibold text-neon hover:text-white transition-colors duration-300 self-start"
              >
                Send a collaboration brief <ArrowRight size={14} />
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
